import type { FinancialForecast, SmartRecommendation, SpendingPattern } from "../stores/aiInsights"
import type { Expense } from "../types/budget"
import type { CategoryBucket } from "../types/firestore"
import { toLocalDateString } from "../utils/localDate"
import { apiRequest } from "./apiClient"

type Category = { id: string; name: string; budget?: number }
type AnalysisType = "patterns" | "recommendations" | "forecast" | "full"

interface AIAnalysisRequest {
  expenses: Expense[]
  categories: Category[]
  monthlyIncome: number
  totalSavings: number
  analysisType: AnalysisType
  provider?: "openai" | "gemini"
}

interface AIAnalysisResponse {
  patterns?: SpendingPattern[]
  recommendations?: SmartRecommendation[]
  forecast?: FinancialForecast
  cachedAt: string
  expiresAt: string
  fromCache: boolean
}

export interface ParsedSetup {
  income: { source: string; amount: number; frequency: string }[]
  accounts: { name: string; type: string; balance: number }[]
  categories: { name: string; budget: number }[]
  expenses: { description: string; amount: number; category: string; date: string; type: "expense" | "income" }[]
}

export interface ParsedExpense {
  description: string
  amount: number
  category: string
  date: string
  type: "expense" | "income"
}

export interface SuggestedCategory {
  name: string
  budget: number
  reason?: string
  /** 50/30/20 lens, server-validated against a closed list. Absent = unsure. */
  bucket?: CategoryBucket
}

export interface ParsedReceipt {
  description: string
  amount: number
  category: string
  date: string
  type: "expense"
  merchant?: string
}

/**
 * What a briefing item can offer to do next. A closed union, validated
 * server-side against the same list — the model picks a key, never a route.
 */
export type BriefingAction =
  | "review_transactions"
  | "open_budgets"
  | "open_transactions"
  | "open_insights"
  | "open_savings"
  | "open_assistant"

export interface BriefingItem {
  type: "warning" | "tip" | "positive"
  text: string
  /** Absent when the model had no useful next step, which is common. */
  action?: BriefingAction
}

export interface Briefing {
  headline: string
  items: BriefingItem[]
  generatedAt: string
  fromCache?: boolean
}

export interface BudgetSuggestion {
  category: string
  suggestedBudget: number
  rationale?: string
  currentBudget?: number | null
  avgMonthlySpend?: number
}

export interface GroceryAlternative {
  name: string
  suggestion: string
  estimatedMonthlySaving: number
  rationale?: string
}

export interface GroceryAlternativeItem {
  name: string
  brand?: string
  category?: string
  unit?: string
  prices: { store: string; price: number }[]
}

export type MealPlanMealType = "breakfast" | "lunch" | "dinner"

export interface MealRecipeIngredient {
  name: string
  amount?: string
}

export interface MealRecipe {
  ingredients: MealRecipeIngredient[]
  steps: string[]
  tip?: string
  timeMinutes?: number
}

export interface MealPlanMeal {
  type: MealPlanMealType
  name: string
  description?: string
  /** Attached server-side once fetched via getMealRecipe — costs quota once per meal. */
  recipe?: MealRecipe
}

export interface MealPlanDay {
  /** Closed key set (monday..sunday) — map via expenses.days.* for display. */
  day: string
  meals: MealPlanMeal[]
}

export interface MealPlanItem {
  name: string
  quantity?: string
  searchTerm?: string
  estimatedPrice: number
  /** Real current price from Kassalapp. Absent = estimate only. */
  storePrice?: number
  matchedProduct?: string
  /** Chain code the price came from; differs from the plan's store when the chosen chain had no hit. */
  pricedAt?: string
}

export interface MealPlan {
  days: MealPlanDay[]
  shoppingList: MealPlanItem[]
  totalEstimate: number
  budget: number
  store: string
  mealTypes: MealPlanMealType[]
  /** Household size the plan was scaled to. Absent on plans generated before it existed. */
  persons?: number
  withinBudget: boolean
  notes?: string
  generatedAt: string
  fromCache?: boolean
}

export interface MealPlanRequest {
  budget: number
  store: string
  mealTypes: MealPlanMealType[]
  persons?: number
  preferences?: string
}

export interface GoalCoachingStats {
  monthsRemaining: number
  currentMonthlyContribution: number
  recommendedMonthlyContribution: number
  probabilityOfSuccess: number
}

export interface GoalCoaching {
  onTrack: boolean
  headline: string
  message: string
  tips: string[]
}

/**
 * AI analysis via the Edge Functions API (POST /api/ai/analyze).
 * Provider keys (OpenAI / Gemini) stay server-side; results are cached for
 * an hour and rate-limited per user.
 */
class BackendAiService {
  private analyze(
    analysisType: AnalysisType,
    expenses: Expense[],
    categories: Category[],
    monthlyIncome: number,
    totalSavings: number,
  ): Promise<AIAnalysisResponse> {
    const payload: AIAnalysisRequest = { expenses, categories, monthlyIncome, totalSavings, analysisType }
    return apiRequest<AIAnalysisResponse>("/api/ai/analyze", { method: "POST", body: payload })
  }

  async analyzePatterns(
    expenses: Expense[],
    categories: Category[],
    monthlyIncome: number,
    totalSavings: number,
  ): Promise<SpendingPattern[]> {
    const result = await this.analyze("patterns", expenses, categories, monthlyIncome, totalSavings)
    return result.patterns || []
  }

  async generateRecommendations(
    expenses: Expense[],
    categories: Category[],
    monthlyIncome: number,
    totalSavings: number,
  ): Promise<SmartRecommendation[]> {
    const result = await this.analyze("recommendations", expenses, categories, monthlyIncome, totalSavings)
    return result.recommendations || []
  }

  async generateForecast(
    expenses: Expense[],
    categories: Category[],
    monthlyIncome: number,
    totalSavings: number,
  ): Promise<FinancialForecast> {
    const result = await this.analyze("forecast", expenses, categories, monthlyIncome, totalSavings)
    return (
      result.forecast || {
        nextMonthBudget: 0,
        predictedSavings: 0,
        riskFactors: [],
        opportunities: [],
      }
    )
  }

  runFullAnalysis(
    expenses: Expense[],
    categories: Category[],
    monthlyIncome: number,
    totalSavings: number,
  ): Promise<AIAnalysisResponse> {
    return this.analyze("full", expenses, categories, monthlyIncome, totalSavings)
  }

  /**
   * Onboarding "smart setup": turn a free-text description of the user's
   * finances into structured income / accounts / categories / expenses they can
   * review before anything is created. Server-side parsing keeps provider keys
   * private and the result clamped to persistable shapes.
   */
  smartSetup(text: string, categories: string[]): Promise<ParsedSetup> {
    const today = toLocalDateString()
    return apiRequest<ParsedSetup>("/api/ai/setup", {
      method: "POST",
      body: { text, today, categories },
      // Mutations get no default timeout, but this one runs during onboarding
      // where the loading phase has no other exit — a hung request would strand
      // a brand-new user on a spinner. 90s is well past a normal completion.
      timeoutMs: 90_000,
    })
  }

  /** Parse a single natural-language transaction ("250kr coffee yesterday"). */
  parseExpense(text: string, categories: string[]): Promise<ParsedExpense> {
    const today = toLocalDateString()
    return apiRequest<ParsedExpense>("/api/ai/parse-expense", {
      method: "POST",
      body: { text, today, categories },
    })
  }

  /** Suggest extra budget categories tailored to a short profile description. */
  suggestCategories(text: string, categories: string[]): Promise<SuggestedCategory[]> {
    return apiRequest<SuggestedCategory[]>("/api/ai/suggest-categories", {
      method: "POST",
      body: { text, categories },
    })
  }

  /**
   * Scan a receipt photo (data: URL) into a structured expense for review.
   * Paid feature — the server enforces the entitlement and quota.
   */
  parseReceipt(imageDataUrl: string, categories: string[], householdId: string | null): Promise<ParsedReceipt> {
    const today = toLocalDateString()
    return apiRequest<ParsedReceipt>("/api/ai/parse-receipt", {
      method: "POST",
      body: { imageDataUrl, categories, today },
      householdId,
    })
  }

  /** Proactive "what changed" briefing for the dashboard. */
  getBriefing(householdId: string | null, refresh = false): Promise<Briefing> {
    return apiRequest<Briefing>(`/api/ai/briefing${refresh ? "?refresh=1" : ""}`, { householdId })
  }

  /** Data-driven, AI-refined monthly budget suggestions per category. */
  suggestBudgets(householdId: string | null): Promise<{ suggestions: BudgetSuggestion[]; generatedAt: string }> {
    return apiRequest<{ suggestions: BudgetSuggestion[]; generatedAt: string }>("/api/ai/suggest-budgets", {
      householdId,
    })
  }

  /** Suggest cheaper grocery options from the user's tracked price data. */
  groceryAlternatives(
    items: GroceryAlternativeItem[],
    householdId: string | null,
  ): Promise<{ suggestions: GroceryAlternative[]; generatedAt: string }> {
    return apiRequest<{ suggestions: GroceryAlternative[]; generatedAt: string }>("/api/ai/grocery-alternatives", {
      method: "POST",
      body: { items },
      householdId,
    })
  }

  /**
   * Plan a week of meals within a grocery budget at a chosen store chain.
   * The server grounds the shopping list in real Kassalapp prices where it
   * can; generation takes a while, hence the long timeout (same reasoning as
   * smartSetup).
   */
  generateMealPlan(params: MealPlanRequest, householdId: string | null): Promise<MealPlan> {
    return apiRequest<MealPlan>("/api/ai/meal-plan", {
      method: "POST",
      body: params,
      householdId,
      timeoutMs: 120_000,
    })
  }

  /** Last generated meal plan for this context (cached a week server-side). */
  getMealPlan(householdId: string | null): Promise<{ plan: MealPlan | null }> {
    return apiRequest<{ plan: MealPlan | null }>("/api/ai/meal-plan", { householdId })
  }

  /**
   * On-demand recipe for one meal of the plan, scaled to the plan's person
   * count. The server patches it into the cached plan, so the cost is one
   * quota unit per meal ever — reopening is free via getMealPlan.
   */
  getMealRecipe(
    params: { mealName: string; mealType?: MealPlanMealType; description?: string; persons?: number },
    householdId: string | null,
  ): Promise<MealRecipe & { mealName: string; persons: number }> {
    return apiRequest<MealRecipe & { mealName: string; persons: number }>("/api/ai/meal-plan/recipe", {
      method: "POST",
      body: params,
      householdId,
      timeoutMs: 60_000,
    })
  }

  /** Turn precomputed savings-goal stats into a short, actionable coaching plan. */
  coachGoal(
    goal: { name: string; targetAmount: number; currentAmount: number; targetDate?: string },
    stats: GoalCoachingStats,
    householdId: string | null,
  ): Promise<GoalCoaching> {
    return apiRequest<GoalCoaching>("/api/ai/goal-coaching", {
      method: "POST",
      body: { goal, stats },
      householdId,
    })
  }
}

export const backendAiService = new BackendAiService()
